import { type JSX } from 'react';
import { snapshotSchema, type Session } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, useMutation } from './components';
import { translations, type Language } from './copy';
import { navigateWorkspace } from './navigation';
import { AccountCredentials } from './account-credentials';
import { Advisers } from './advisers';
import { accountBotsSchema } from './adviser-crm';
import { Connections, Usage } from './settings';
import { Billing } from './billing';
import { AccountCalendars } from './account-calendars';
import { Privacy } from './privacy';
import { PlanCapacityNotice } from './plan-capacity';
import type { View } from './app';

export const accountViews: View[] = [
  'accountAiProviders',
  'accountTeam',
  'connections',
  'usage',
  'privacy',
];
const connectionsSchema = snapshotSchema.pick({
  providers: true,
  connections: true,
  mailFailures: true,
  businessConnections: true,
});
export function AccountWorkspace({
  companyId,
  selectedBotId,
  session,
  view,
  language,
  onNavigate,
}: {
  companyId: string;
  selectedBotId: string;
  session: Session;
  view: View;
  language: Language;
  onNavigate: (view: View) => void;
}): JSX.Element {
  const copy = translations[language];
  const bots = useRemote(`/v1/accounts/${companyId}/bots`, accountBotsSchema);
  const mutation = useMutation();
  const company = session.accounts.find(
    (account) => account.companyId === companyId
  );
  const bot = selectedBotId
    ? bots.data?.bots.find((bot) => bot.id === selectedBotId)
    : bots.data?.bots[0];
  return (
    <>
      <section className="account-heading">
        <h1>{copy.accountSettings}</h1>
        <p>{copy.accountSettingsHelp}</p>
        <Field label={copy.companyAccount}>
          <select
            value={companyId}
            onChange={(event) =>
              void mutation.run(() =>
                navigateWorkspace({
                  companyId: event.target.value,
                  botId: null,
                  step: null,
                })
              )
            }
          >
            {session.accounts
              .filter((account) => ['owner', 'admin'].includes(account.role))
              .map((account) => (
                <option value={account.companyId} key={account.companyId}>
                  {account.name}
                </option>
              ))}
          </select>
        </Field>
      </section>
      <nav className="workspace-tabs" aria-label={copy.accountSettings}>
        {accountViews.map((item) => (
          <button
            key={item}
            aria-current={view === item ? 'page' : undefined}
            onClick={() => onNavigate(item)}
          >
            {copy[item]}
          </button>
        ))}
      </nav>
      <ErrorNotice
        error={bots.error ?? mutation.error}
        copy={copy}
        onRetry={bots.reload}
      />
      {bots.loading && !bots.data && (
        <p className="page-loading">{copy.loading}</p>
      )}
      {bots.data && !bot && (
        <section className="workspace-empty">
          <p role="alert">
            {selectedBotId ? copy.ACCOUNT_BOT_SCOPE_INVALID : copy.noBots}
          </p>
          {selectedBotId && (
            <button
              onClick={() =>
                void mutation.run(() => navigateWorkspace({ botId: null }))
              }
            >
              {copy.selectBot}
            </button>
          )}
        </section>
      )}
      {company && bot && (
        <>
          {['connections', 'usage', 'privacy'].includes(view) && (
            <PlanCapacityNotice
              key={`${companyId}/${bot.id}`}
              path={`/v1/accounts/${companyId}/bots/${bot.id}/capacity`}
              copy={copy}
              language={language}
              onOpen={view === 'usage' ? null : () => onNavigate('usage')}
            />
          )}
          {['connections', 'usage', 'privacy'].includes(view) && (
            <div className="account-assistant-choice">
              <Field label={copy.appliesToAssistant}>
                <select
                  value={bot.id}
                  onChange={(event) =>
                    void mutation.run(() =>
                      navigateWorkspace({ botId: event.target.value })
                    )
                  }
                >
                  {bots.data!.bots.map((bot) => (
                    <option key={bot.id} value={bot.id}>
                      {bot.name}
                    </option>
                  ))}
                </select>
              </Field>
              <p className="small muted">{copy.accountAssistantScopeHelp}</p>
            </div>
          )}
          <AccountSection
            key={`${companyId}/${bot.id}/${view}`}
            companyId={companyId}
            botId={bot.id}
            session={session}
            view={view}
            language={language}
          />
        </>
      )}
    </>
  );
}
function AccountSection({
  companyId,
  botId,
  session,
  view,
  language,
}: {
  companyId: string;
  botId: string;
  session: Session;
  view: View;
  language: Language;
}): JSX.Element {
  const copy = translations[language],
    scope = { companyId, botId };
  const apiBase = `/v1/accounts/${companyId}/bots/${botId}/`;
  const connections = useRemote(
    view === 'connections' ? `${apiBase}connections` : null,
    connectionsSchema
  );
  const member = session.memberships.find(
    (member) => member.company_id === companyId && member.bot_id === botId
  );
  if (view === 'accountAiProviders')
    return <AccountCredentials scope={scope} session={session} copy={copy} />;
  if (view === 'accountTeam')
    return (
      <Advisers
        scope={scope}
        session={session}
        copy={copy}
        language={language}
      />
    );
  if (view === 'connections')
    return (
      <>
        <ErrorNotice
          error={connections.error}
          copy={copy}
          onRetry={connections.reload}
        />
        {connections.loading && !connections.data && (
          <p className="page-loading">{copy.loading}</p>
        )}
        {connections.data && (
          <AccountCalendars
            scope={scope}
            session={session}
            copy={copy}
            providers={connections.data.providers}
          />
        )}
        {connections.data && (
          <Connections
            scope={scope}
            session={session}
            copy={copy}
            language={language}
            canAdmin
            snapshot={connections.data}
            reload={connections.reload}
            apiBase={apiBase}
            showSourceOrigins={
              !!member && ['owner', 'admin'].includes(member.role)
            }
          />
        )}
      </>
    );
  if (view === 'usage')
    return (
      <div className="settings-content">
        <Usage
          scope={scope}
          copy={copy}
          language={language}
          apiBase={apiBase}
        />
        <Billing
          scope={scope}
          session={session}
          copy={copy}
          language={language}
          canWrite
          apiBase={apiBase}
        />
      </div>
    );
  return member && ['owner', 'admin'].includes(member.role) ? (
    <Privacy scope={scope} session={session} copy={copy} language={language} />
  ) : (
    <section className="settings-content">
      <h1>{copy.privacy}</h1>
      <p role="alert">{copy.privacyAccountAccessHelp}</p>
    </section>
  );
}
